import type {
  UserInfo,
  EnvironmentsResponse,
  PromotionPreviewResponse,
  PromotionApplyResponse,
  PullRequestsResponse,
  PromotionRequest,
  MfeVersionsResponse,
  ProductsResponse,
  AuditLogResponse,
  PromotionPatchRequest,
  PromotionPatchResponse,
  PromotionVersionsResponse,
  RegistrationRequest,
  RegistrationResponse,
  ServicesResponse,
  CreateBundleRequest,
  CreateBundleResponse,
  TenantInfo,
  InstanceInfo,
  PlmProductInfo,
  BundleSummary,
  CreateTenantRequest,
  CreateInstanceRequest,
  CreatePlmProductRequest,
  UpdatePlmProductRequest,
  DeleteBundleRequest,
  DeleteBundleResponse,
  FlagsResponse,
  CreateFlagRequest,
  UpdateFlagRequest,
  FlagCell,
  FlagsAuditResponse,
  EnableFlagsResponse,
  FlagEnv,
} from "./types";

const BASE = "/api";
export const AUTH_EXPIRED_EVENT = "dashboard-auth-expired";
export const browserNavigation = {
  assign(url: string) {
    window.location.assign(url);
  },
};

let authRevalidationPromise: Promise<boolean> | null = null;
let authExpiredDispatched = false;

function buildRequestOptions(options?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...options,
    headers: {
      "content-type": "application/json",
      ...options?.headers,
    },
  };
}

function performFetch(path: string, options?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, buildRequestOptions(options));
}

function dispatchAuthExpired() {
  if (authExpiredDispatched) return;
  authExpiredDispatched = true;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

async function revalidateAuth(): Promise<boolean> {
  if (!authRevalidationPromise) {
    authRevalidationPromise = (async () => {
      const response = await performFetch("/auth/me");
      const isAuthenticated = response.ok;

      if (isAuthenticated) {
        authExpiredDispatched = false;
      }

      return isAuthenticated;
    })().finally(() => {
      authRevalidationPromise = null;
    });
  }

  return authRevalidationPromise;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new ApiError(body.error || response.statusText, response.status);
  }

  return response.json();
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await performFetch(path, options);

  if (response.status === 401) {
    if (path !== "/auth/me" && path !== "/auth/logout") {
      const isAuthenticated = await revalidateAuth();

      if (isAuthenticated) {
        const retryResponse = await performFetch(path, options);

        if (retryResponse.status !== 401) {
          return parseResponse<T>(retryResponse);
        }
      }
    }

    dispatchAuthExpired();
    throw new ApiError("Not authenticated", 401);
  }

  return parseResponse<T>(response);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function resetAuthStateForTests() {
  authRevalidationPromise = null;
  authExpiredDispatched = false;
}

export const api = {
  getUser(): Promise<UserInfo> {
    return request("/auth/me");
  },

  login() {
    try {
      const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      sessionStorage.setItem("auth-return-url", returnUrl || "/");
      sessionStorage.setItem("auth-redirect-ts", String(Date.now()));
    } catch {
      // Return URL persistence is best-effort only.
    }

    browserNavigation.assign(`${BASE}/auth/login`);
  },

  async logout(): Promise<void> {
    await request("/auth/logout", { method: "POST" });
  },

  getEnvironments(): Promise<EnvironmentsResponse> {
    return request("/environments");
  },

  getServices(): Promise<ServicesResponse> {
    return request("/services");
  },

  previewPromotion(requestBody: PromotionRequest): Promise<PromotionPreviewResponse> {
    return request("/promotions/preview", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });
  },

  applyPromotion(requestBody: PromotionRequest): Promise<PromotionApplyResponse> {
    return request("/promotions/apply", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });
  },

  getPullRequests(): Promise<PullRequestsResponse> {
    return request("/pull-requests");
  },

  getMfeVersions(mfe: string, includePrerelease = false): Promise<MfeVersionsResponse> {
    const params = new URLSearchParams({ mfe });
    if (includePrerelease) params.set("includePrerelease", "true");
    return request(`/mfe-versions?${params}`);
  },

  getProducts(): Promise<ProductsResponse> {
    return request("/products");
  },

  // Feature flags
  getFlags(product: string): Promise<FlagsResponse> {
    return request(`/flags?${new URLSearchParams({ product })}`);
  },

  createFlag(body: CreateFlagRequest): Promise<{ product: string; key: string }> {
    return request("/flags", { method: "POST", body: JSON.stringify(body) });
  },

  updateFlag(key: string, body: UpdateFlagRequest): Promise<FlagCell & { product: string; key: string; env: FlagEnv }> {
    return request(`/flags/${key}`, { method: "PATCH", body: JSON.stringify(body) });
  },

  deleteFlag(key: string, product: string): Promise<{ deleted: boolean }> {
    return request(`/flags/${key}?${new URLSearchParams({ product })}`, { method: "DELETE" });
  },

  getFlagsAudit(product: string, page = 1, limit = 25): Promise<FlagsAuditResponse> {
    return request(`/flags/audit?${new URLSearchParams({ product, page: String(page), limit: String(limit) })}`);
  },

  enableFlags(product: string): Promise<EnableFlagsResponse> {
    return request("/flags/enable", { method: "POST", body: JSON.stringify({ product }) });
  },

  getAuditLog(page = 1, limit = 25, mfe?: string): Promise<AuditLogResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (mfe) params.set("mfe", mfe);
    return request(`/audit-log?${params}`);
  },

  patchPromotion(prNumber: number, body: PromotionPatchRequest): Promise<PromotionPatchResponse> {
    return request(`/promotions/${prNumber}/patch`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  getPromotionVersions(prNumber: number): Promise<PromotionVersionsResponse> {
    return request(`/promotions/${prNumber}/versions`);
  },

  registerMfe(body: RegistrationRequest): Promise<RegistrationResponse> {
    return request("/registrations/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Tree API methods
  getTenants(): Promise<{ tenants: TenantInfo[] }> {
    return request("/tenants");
  },

  createTenant(body: CreateTenantRequest): Promise<{ tenant: TenantInfo }> {
    return request("/tenants", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  deleteTenant(name: string): Promise<{ deleted: string }> {
    const params = new URLSearchParams({ name });
    return request(`/tenants?${params}`, { method: "DELETE" });
  },

  getTenantInstances(tenant: string): Promise<{ instances: InstanceInfo[] }> {
    const params = new URLSearchParams({ tenant });
    return request(`/instances?${params}`);
  },

  createInstance(body: CreateInstanceRequest): Promise<{ instance: InstanceInfo }> {
    return request("/instances", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  deleteInstance(tenant: string, name: string): Promise<{ deleted: string }> {
    const params = new URLSearchParams({ tenant, name });
    return request(`/instances?${params}`, { method: "DELETE" });
  },

  getPlmProducts(tenant: string, instance: string): Promise<{ products: PlmProductInfo[] }> {
    const params = new URLSearchParams({ tenant, instance });
    return request(`/plm-products?${params}`);
  },

  createPlmProduct(body: CreatePlmProductRequest): Promise<{ product: PlmProductInfo }> {
    return request("/plm-products", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  updatePlmProduct(body: UpdatePlmProductRequest): Promise<{ product: PlmProductInfo }> {
    return request("/plm-products", {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  deletePlmProduct(tenant: string, instance: string, name: string): Promise<{ deleted: string }> {
    const params = new URLSearchParams({ tenant, instance, name });
    return request(`/plm-products?${params}`, { method: "DELETE" });
  },

  getInstanceBundles(params: { tenant: string; instance: string; product: string }): Promise<{ bundles: BundleSummary[] }> {
    const searchParams = new URLSearchParams(params);
    return request(`/bundles?${searchParams}`);
  },

  createInstanceBundle(body: CreateBundleRequest): Promise<CreateBundleResponse> {
    return request("/bundles/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  deleteInstanceBundle(body: DeleteBundleRequest): Promise<DeleteBundleResponse> {
    return request("/bundles/delete", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};
